
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService } from '../../core/services/authentication/authentication.service';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonComponent} from '../../shared/components/button/button.component';


@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent
],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  private readonly returnUrl: string;
  loginForm!: FormGroup;
  mfaForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  requireMfa = false;
  challengeToken = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthenticationService
  ) {
    // Get the return URL from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  ngOnInit(): void {
    this.initForms();

    // Check if there's a stored challenge token that hasn't expired
    const storedToken = this.authService.getChallengeToken();
    if (storedToken) {
      this.challengeToken = storedToken;
      this.requireMfa = true;
    }
  }

  initForms(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [false]
    });

    this.mfaForm = this.formBuilder.group({
      mfaCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  onLoginSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const { email, password, rememberMe } = this.loginForm.value;
    this.authService.login(email, password, rememberMe).subscribe({
        next: (response) => {
          this.isLoading = false;

          if ('isRequireTwoFactor' in response && response.isRequireTwoFactor) {
            this.requireMfa = true;
            this.challengeToken = response.challengeToken;
          } else {
            // Login successful without MFA
            this.router.navigateByUrl(this.returnUrl);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message;
        }
      });
  }

  onMfaSubmit(): void {
    if (this.mfaForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const { mfaCode } = this.mfaForm.value;

    this.authService.validateMfa(this.challengeToken, mfaCode).subscribe({
        next: () => {
          this.isLoading = false;
          this.requireMfa = false;
          this.challengeToken = '';
          this.router.navigateByUrl(this.returnUrl);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message;

          // If the challenge token has expired, go back to login form
          if (error.message.includes('Challenge token has expired')) {
            this.requireMfa = false;
            this.challengeToken = '';
          }
        }
      });
  }

  goBack(): void {
    this.router.navigateByUrl(this.returnUrl);
  }

  backToLogin(): void {
    this.requireMfa = false;
    this.challengeToken = '';
    this.errorMessage = '';
  }
}
